import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Popover, PopoverContent, PopoverTrigger, Tooltip, cn } from '@playerbase/ui';

import { useAuth } from '../../auth/use-auth.js';
import { useShell } from '../use-shell.js';

function deriveDisplayName(email: string | null | undefined): string {
  if (email === null || email === undefined || email.length === 0) return 'You';
  const local = email.split('@')[0] ?? email;
  return local.replace(/[._-]+/g, ' ').replace(/\s+/g, ' ').trim() || email;
}

type UserMenuProps = {
  collapsed?: boolean;
};

export function UserMenu({ collapsed = false }: UserMenuProps): JSX.Element | null {
  const { user, signOut } = useAuth();
  const { activeStudio } = useShell();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (user === null) return null;

  const displayName = deriveDisplayName(user.email);

  function goToAccount(): void {
    setOpen(false);
    navigate(`/s/${activeStudio.shortId}/account`);
  }

  async function handleSignOut(): Promise<void> {
    setOpen(false);
    await signOut();
  }

  const popoverContent = (
    <PopoverContent
      className="w-[14rem]"
      side={collapsed ? 'right' : 'top'}
      align={collapsed ? 'end' : 'start'}
    >
      <button
        type="button"
        onClick={goToAccount}
        className="flex w-full items-center rounded-sm px-3 py-2 text-left text-sm text-text hover:bg-surface-2"
      >
        Account settings
      </button>
      <button
        type="button"
        onClick={() => {
          void handleSignOut();
        }}
        className="flex w-full items-center rounded-sm px-3 py-2 text-left text-sm text-text hover:bg-surface-2"
      >
        Sign out
      </button>
    </PopoverContent>
  );

  if (collapsed) {
    return (
      <div className="flex justify-center px-2 pt-2 pb-3">
        <Popover open={open} onOpenChange={setOpen}>
          <Tooltip content={displayName} side="right" delayMs={120}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={`Account: ${displayName}`}
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-md',
                  'text-text transition-colors hover:bg-surface-2',
                )}
              >
                <Avatar name={displayName} size="md" />
              </button>
            </PopoverTrigger>
          </Tooltip>
          {popoverContent}
        </Popover>
      </div>
    );
  }

  return (
    <div className="px-3 pt-2 pb-3">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex w-full items-center gap-2 rounded-sm px-2 py-2',
              'text-left text-sm text-text transition-colors hover:bg-surface-2',
            )}
          >
            <Avatar name={displayName} size="md" />
            <div className="flex flex-1 flex-col leading-tight overflow-hidden">
              <span className="truncate">{displayName}</span>
              {user.email !== undefined && (
                <span className="truncate text-[11px] text-text-muted">
                  {user.email}
                </span>
              )}
            </div>
          </button>
        </PopoverTrigger>
        {popoverContent}
      </Popover>
    </div>
  );
}
